import React, { memo, useState, useMemo, useCallback, useEffect } from "react";
import { Search, Plus, X } from "lucide-react";
import UserCard from "./UserCard";
import { Button } from "../ui/button";
import { useLazySearchUsersQuery } from "../../Services/modules/sponsors";
import { useDebounce } from "../../hooks/useDebounce";
import LoadingState from "../generic/LoadingState";
import EmptyState from "../generic/EmptyState";

const UserSearchAdd = memo(
  ({
    onAddUsers,
    selectedUsers = [],
    excludeUsers = [],
    placeholder = "Rechercher un utilisateur...",
    maxResults = 10,
    maxSelection = 1,
    sectionTitle = "Recherche",
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [localSelectedUsers, setLocalSelectedUsers] = useState([]);

    const debouncedSearch = useDebounce(searchTerm, 300);

    const [searchUsers, { data: searchResults, isLoading, error }] =
      useLazySearchUsersQuery();

    useEffect(() => {
      if (debouncedSearch.length >= 2) {
        setIsSearching(true);
        searchUsers({ search: debouncedSearch, limit: maxResults }).finally(
          () => setIsSearching(false)
        );
      }
    }, [debouncedSearch, searchUsers, maxResults]);

    const filteredUsers = useMemo(() => {
      if (!searchResults?.users) return [];

      return searchResults.users.filter(
        (user) =>
          !excludeUsers.some(
            (excludeUser) =>
              excludeUser.uid === user.uid || excludeUser.id === user.id
          ) &&
          !localSelectedUsers.some(
            (selectedUser) =>
              selectedUser.uid === user.uid || selectedUser.id === user.id
          )
      );
    }, [searchResults?.users, excludeUsers, localSelectedUsers]);

    const handleUserSelect = useCallback(
      (user) => {
        if (maxSelection === 1) {
          setLocalSelectedUsers([user]);
        } else {
          setLocalSelectedUsers((prev) => [...prev, user]);
        }
      },
      [maxSelection]
    );

    const handleRemoveUser = useCallback((userToRemove) => {
      setLocalSelectedUsers((prev) =>
        prev.filter(
          (user) => user.uid !== userToRemove.uid && user.id !== userToRemove.id
        )
      );
    }, []);

    const handleAddSelected = useCallback(() => {
      if (localSelectedUsers.length > 0) {
        onAddUsers(localSelectedUsers);
        setLocalSelectedUsers([]);
      }
    }, [localSelectedUsers, onAddUsers]);

    const searchContent = useMemo(() => {
      if (isLoading || isSearching) {
        return <LoadingState text="Recherche en cours..." />;
      }

      if (error) {
        return <EmptyState title="Erreur lors de la recherche" />;
      }

      if (!searchResults?.users || searchResults.users.length === 0) {
        return debouncedSearch.length >= 2 ? (
          <EmptyState title="Aucun utilisateur trouvé" />
        ) : null;
      }

      return (
        <div className="overflow-y-auto space-y-2 max-h-40">
          {filteredUsers.map((user) => (
            <div
              key={user.uid || user.id}
              className="flex gap-3 items-center p-3 bg-white rounded border transition-colors cursor-pointer hover:bg-gray-50"
              onClick={() => handleUserSelect(user)}
            >
              <Plus className="w-4 h-4 text-green-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email}
                </div>
                <div className="text-xs text-gray-500">{user.email}</div>
                {user.businessName && (
                  <div className="text-xs text-gray-400">
                    {user.businessName}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }, [
      isLoading,
      isSearching,
      error,
      searchResults?.users,
      filteredUsers,
      debouncedSearch.length,
      handleUserSelect,
    ]);

    const selectedUsersContent = useMemo(() => {
      if (localSelectedUsers.length === 0) return null;

      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            Utilisateurs sélectionnés ({localSelectedUsers.length})
          </div>
          <div className="overflow-y-auto space-y-2 max-h-40">
            {localSelectedUsers.map((user) => (
              <div key={user.uid || user.id} className="relative">
                <UserCard user={user} className="pr-8" />
                <button
                  onClick={() => handleRemoveUser(user)}
                  className="absolute top-2 right-2 p-1 bg-red-100 rounded-full transition-colors hover:bg-red-200"
                >
                  <X className="w-3 h-3 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }, [localSelectedUsers, handleRemoveUser]);

    const addButton = useMemo(() => {
      if (localSelectedUsers.length === 0) return null;

      return (
        <Button
          onClick={handleAddSelected}
          className="flex gap-2 items-center w-full"
          size="sm"
        >
          <Plus className="w-4 h-4" />
          Ajouter ({localSelectedUsers.length})
        </Button>
      );
    }, [localSelectedUsers.length, handleAddSelected]);

    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h4 className="font-medium">{sectionTitle}</h4>
          {addButton}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            className="py-2 pr-4 pl-10 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {searchContent}
        {selectedUsersContent}
      </div>
    );
  }
);

UserSearchAdd.displayName = "UserSearchAdd";

export default UserSearchAdd;
